import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import { LocalStorageKeys } from '../types/local-storage'
import { useAuth } from './useAuth'
import { userQueries } from '../api/users'
import { getLanguage } from '../helpers/i18n'

interface AppTranslationProviderProps {
  children: React.ReactNode
  isStorybook?: boolean
}

interface IAppTranslationContext {
  translate: TFunction<'translation', undefined>
}

const AppTranslationContext = createContext<IAppTranslationContext>({
  // eslint-disable-next-line
  // @ts-expect-error do not expect any type here.
  translate: () => null,
})

const AppTranslationProvider = ({
  children,
  isStorybook = false,
}: AppTranslationProviderProps): JSX.Element => {
  const { userId } = useAuth()
  const { t, i18n } = useTranslation()
  const { data: user } = useQuery(userQueries.getUser(userId))
  const value = useMemo(() => ({ translate: t }), [t])

  // Return early if used with storybook to avoid store operations
  if (isStorybook) {
    return (
      <AppTranslationContext.Provider value={value}>
        {children}
      </AppTranslationContext.Provider>
    )
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getLanguagei18n = useCallback((): string => {
    const localStorageValue = localStorage.getItem(LocalStorageKeys.LOCALE)

    if (user && user.language) {
      return user.language
    }
    if (localStorageValue) {
      return localStorageValue
    }
    return 'sv'
  }, [user])

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    i18n.changeLanguage(getLanguagei18n())
    document.documentElement.lang = getLanguage(i18n.language)
  }, [userId, i18n, getLanguagei18n])

  return (
    <AppTranslationContext.Provider value={value}>
      {children}
    </AppTranslationContext.Provider>
  )
}

AppTranslationProvider.displayName = 'AppTranslationProvider'
export default AppTranslationProvider

export const useAppTranslation = (): IAppTranslationContext =>
  useContext(AppTranslationContext)
